import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, Transition as _Transition, renderList as _renderList, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0bddbf62"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "is-flex-direction-column" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { class: "columns is-vcentered" }
const _hoisted_4 = { class: "column is-12 is-flex is-align-items-center" }
const _hoisted_5 = {
  id: "page-title",
  class: "title mb-0 is-4 is-uppercase has-text-weight-medium is-inline"
}
const _hoisted_6 = { class: "column is-6" }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = {
  key: 1,
  class: "column is-6"
}
const _hoisted_9 = { class: "is-size-6 has-text-grey is-uppercase has-text-weight-medium mb-3" }
const _hoisted_10 = { class: "is-size-7 has-text-grey mt-2" }

import { CircleCheck, Loading, Medal, WarningFilled } from '@element-plus/icons-vue';
import { debounce } from 'debounce';
import { ElMessage } from 'element-plus';
import { computed, onMounted, Ref, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import CustomModal from '@/components/CustomModal.vue';
import PasswordView from '@/components/PasswordView.vue';
import { canAssignMemberRole, canRequirePasswordReset } from '@/helpers/permission';
import { Litigation } from '@/Litigation/litigation.entity';
import LitigationService from '@/Litigation/litigation.service';
import { Member, MemberRole } from '@/Member/member.entity';
import MemberService from '@/Member/member.service';

const SAVE_INDICATOR_TIMEOUT = 5000;

type MemberFormModel = Omit<Member, 'litigations'> & {
    litigations: Array<string>;
};
type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export default /*#__PURE__*/_defineComponent({
  __name: 'MemberForm',
  props: {
    memberId: {
        type: String,
        required: true,
    },
},
  setup(__props) {

const props = __props;

const { t } = useI18n();

const memberFormModel: Ref<MemberFormModel> = ref();
const litigations: Ref<Array<Litigation>> = ref();
const resetPassword: Ref<string | null> = ref(null);
const isPasswordViewOpen: Ref<boolean> = ref(false);
const isConfirmResetOpen: Ref<boolean> = ref(false);

const saveState: Ref<SaveState> = ref('idle');
let savedIndicatorTimeout: ReturnType<typeof setTimeout> | undefined;

const saveIndicatorLabel = computed(() => {
    switch (saveState.value) {
        case 'saving':
            return t('memberDetailsForm.saving');
        case 'saved':
            return t('memberDetailsForm.saved');
        case 'error':
            return t('memberDetailsForm.saveError');
        default:
            return '';
    }
});

const showPasswordReset = computed(() => canRequirePasswordReset(memberFormModel.value));



const { memberId } = toRefs(props);

const getMemberFormModel = async (memberId: string): Promise<MemberFormModel> => {
    const memberService = new MemberService();
    const member = await memberService.find(memberId);
    const { litigations, ...model } = member;

    return {
        ...model,
        litigations: (litigations || []).map((litigation) => litigation.litigationId ?? ''),
    };
};

const getLitigations = async (): Promise<Array<Litigation>> => {
    const litigationService = new LitigationService();
    return litigationService.findAll();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const debounceSaveMember = debounce(function debounceSaveMember(this: any) {
    saveMember();
}, 1000);

const saveMember = async (): Promise<void> => {
    if (!memberFormModel.value) {
        return;
    }

    const memberService = new MemberService();

    clearTimeout(savedIndicatorTimeout);
    saveState.value = 'saving';

    try {
        await memberService.update(memberId.value, memberFormModel.value);
        saveState.value = 'saved';
        savedIndicatorTimeout = setTimeout(() => {
            if (saveState.value === 'saved') saveState.value = 'idle';
        }, SAVE_INDICATOR_TIMEOUT);
    } catch (error) {
        saveState.value = 'error';
        ElMessage.error(t('common.permissionError'));
    }
};

const requirePasswordReset = async (): Promise<void> => {
    const memberService = new MemberService();
    try {
        const result = await memberService.requirePasswordReset(memberId.value);
        resetPassword.value = result.password ?? null;
        isPasswordViewOpen.value = true;
    } catch (error) {
        ElMessage.error(t('common.unknownError'));
    }
};

const confirmRequirePasswordReset = async (): Promise<void> => {
    isConfirmResetOpen.value = false;
    await requirePasswordReset();
};

onMounted(async () => {
    memberFormModel.value = await getMemberFormModel(memberId.value);
    litigations.value = await getLitigations();
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_radio_button = _resolveComponent("el-radio-button")!
  const _component_el_radio_group = _resolveComponent("el-radio-group")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_switch = _resolveComponent("el-switch")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_button = _resolveComponent("el-button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (memberFormModel.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("p", _hoisted_5, [
                _createVNode(_component_router_link, { to: { name: 'member-list' } }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('listMembers.members')), 1)
                  ]),
                  _: 1
                }),
                _createTextVNode(" / " + _toDisplayString(memberFormModel.value.email), 1)
              ]),
              _createVNode(_Transition, { name: "fade" }, {
                default: _withCtx(() => [
                  (saveState.value !== 'idle')
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        class: _normalizeClass(["is-flex is-align-items-center ml-4 is-6", {
                                'has-text-grey': saveState.value === 'saving',
                                'has-text-primary': saveState.value === 'saved',
                                'has-text-danger': saveState.value === 'error',
                            }])
                      }, [
                        (saveState.value === 'saving')
                          ? (_openBlock(), _createBlock(_component_el_icon, {
                              key: 0,
                              class: "is-loading mr-1"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(Loading))
                              ]),
                              _: 1
                            }))
                          : (saveState.value === 'saved')
                            ? (_openBlock(), _createBlock(_component_el_icon, {
                                key: 1,
                                class: "mr-1"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(CircleCheck))
                                ]),
                                _: 1
                              }))
                            : (_openBlock(), _createBlock(_component_el_icon, {
                                key: 2,
                                class: "mr-1"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(WarningFilled))
                                ]),
                                _: 1
                              })),
                        _createTextVNode(" " + _toDisplayString(saveIndicatorLabel.value), 1)
                      ], 2))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ])
          ]),
          _createElementVNode("section", _hoisted_6, [
            _createVNode(_component_el_form, {
              model: memberFormModel.value,
              "label-width": "120px",
              onInput: _cache[8] || (_cache[8] = ($event: any) => (_unref(debounceSaveMember)()))
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_form_item, {
                  label: _unref(t)('listMembers.email'),
                  prop: "email",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_input, {
                      modelValue: memberFormModel.value.email,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((memberFormModel.value.email) = $event)),
                      type: "email"
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }, 8, ["label"]),
                _createVNode(_component_el_form_item, {
                  label: _unref(t)('listMembers.firstName'),
                  prop: "firstName",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_input, {
                      modelValue: memberFormModel.value.firstName,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((memberFormModel.value.firstName) = $event))
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }, 8, ["label"]),
                _createVNode(_component_el_form_item, {
                  label: _unref(t)('listMembers.lastName'),
                  prop: "lastName",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_input, {
                      modelValue: memberFormModel.value.lastName,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((memberFormModel.value.lastName) = $event))
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }, 8, ["label"]),
                _createVNode(_component_el_form_item, {
                  label: _unref(t)('listMembers.role'),
                  prop: "role",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_radio_group, {
                      modelValue: memberFormModel.value.role,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((memberFormModel.value.role) = $event))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_radio_button, {
                          disabled: !_unref(canAssignMemberRole)(memberFormModel.value, _unref(MemberRole).USER),
                          label: _unref(MemberRole).USER
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(t)('listMembers.roles.user')), 1)
                          ]),
                          _: 1
                        }, 8, ["disabled", "label"]),
                        _createVNode(_component_el_radio_button, {
                          disabled: !_unref(canAssignMemberRole)(memberFormModel.value, _unref(MemberRole).EXPERT),
                          label: _unref(MemberRole).EXPERT
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(t)('listMembers.roles.expert')), 1)
                          ]),
                          _: 1
                        }, 8, ["disabled", "label"]),
                        _createVNode(_component_el_radio_button, {
                          disabled: !_unref(canAssignMemberRole)(memberFormModel.value, _unref(MemberRole).SUPERVISOR),
                          label: _unref(MemberRole).SUPERVISOR
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(t)('listMembers.roles.supervisor')) + " ", 1),
                            _createVNode(_component_el_icon, null, {
                              default: _withCtx(() => [
                                _createVNode(_unref(Medal))
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["disabled", "label"]),
                        _createVNode(_component_el_radio_button, {
                          disabled: !_unref(canAssignMemberRole)(memberFormModel.value, _unref(MemberRole).ADMIN),
                          label: _unref(MemberRole).ADMIN
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(t)('listMembers.roles.admin')) + " ", 1),
                            _createVNode(_component_el_icon, null, {
                              default: _withCtx(() => [
                                _createVNode(_unref(Medal))
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["disabled", "label"])
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                }, 8, ["label"]),
                (litigations.value && memberFormModel.value.role !== _unref(MemberRole).ADMIN)
                  ? (_openBlock(), _createBlock(_component_el_form_item, {
                      key: 0,
                      label: _unref(t)('listMembers.litigations'),
                      prop: "litigations"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_select, {
                          modelValue: memberFormModel.value.litigations,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((memberFormModel.value.litigations) = $event)),
                          multiple: "",
                          onChange: _cache[5] || (_cache[5] = ($event: any) => (saveMember()))
                        }, {
                          default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(litigations.value, (litigation) => {
                              return (_openBlock(), _createBlock(_component_el_option, {
                                key: litigation.litigationId,
                                label: litigation.name,
                                value: litigation.litigationId
                              }, null, 8, ["label", "value"]))
                            }), 128))
                          ]),
                          _: 1
                        }, 8, ["modelValue"])
                      ]),
                      _: 1
                    }, 8, ["label"]))
                  : _createCommentVNode("", true),
                _createVNode(_component_el_form_item, {
                  label: _unref(t)('listMembers.isEnabled'),
                  prop: "isEnabled"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_switch, {
                      modelValue: memberFormModel.value.isEnabled,
                      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((memberFormModel.value.isEnabled) = $event)),
                      onChange: _cache[7] || (_cache[7] = ($event: any) => (saveMember()))
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }, 8, ["label"])
              ]),
              _: 1
            }, 8, ["model"])
          ]),
          (showPasswordReset.value)
            ? (_openBlock(), _createElementBlock("hr", _hoisted_7))
            : _createCommentVNode("", true),
          (showPasswordReset.value)
            ? (_openBlock(), _createElementBlock("section", _hoisted_8, [
                _createElementVNode("h2", _hoisted_9, _toDisplayString(_unref(t)('memberDetailsForm.security')), 1),
                _createElementVNode("div", {
                  class: "reset-password has-text-primary is-size-6 is-inline-flex p-3 has-text-weight-semibold is-capitalized",
                  onClick: _cache[9] || (_cache[9] = ($event: any) => (isConfirmResetOpen.value = true))
                }, _toDisplayString(_unref(t)('memberDetailsForm.requirePasswordReset')), 1),
                _createElementVNode("p", _hoisted_10, _toDisplayString(_unref(t)('memberDetailsForm.requirePasswordResetHelp')), 1)
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (isConfirmResetOpen.value)
      ? (_openBlock(), _createBlock(CustomModal, {
          key: 1,
          onClose: _cache[11] || (_cache[11] = ($event: any) => (isConfirmResetOpen.value = false))
        }, {
          "modal-header": _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('memberDetailsForm.requirePasswordResetConfirmTitle')), 1)
          ]),
          "modal-footer": _withCtx(() => [
            _createVNode(_component_el_button, {
              size: "small",
              class: "button is-secondary is-radius-small",
              onClick: _cache[10] || (_cache[10] = ($event: any) => (isConfirmResetOpen.value = false))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('listMembers.cancel')), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_el_button, {
              size: "small",
              class: "button is-primary is-radius-small",
              onClick: confirmRequirePasswordReset
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('common.confirmDialogTitle')), 1)
              ]),
              _: 1
            })
          ]),
          default: _withCtx(() => [
            _createElementVNode("p", null, _toDisplayString(_unref(t)('memberDetailsForm.requirePasswordResetConfirmMessage', { email: memberFormModel.value?.email })), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (isPasswordViewOpen.value && resetPassword.value)
      ? (_openBlock(), _createBlock(PasswordView, {
          key: 2,
          email: memberFormModel.value?.email ?? '',
          password: resetPassword.value ?? '',
          onClose: _cache[12] || (_cache[12] = ($event: any) => {
                isPasswordViewOpen.value = false;
                resetPassword.value = null;
            })
        }, null, 8, ["email", "password"]))
      : _createCommentVNode("", true)
  ]))
}
}

})